import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileCatOpen, setIsMobileCatOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/98 backdrop-blur-md border-b border-secondary-200/60'
          : 'bg-background-50/90 backdrop-blur-md border-b border-secondary-100/40'
      }`}
    >
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link
            to="/"
            className="text-foreground-950 font-bold text-xl tracking-tight whitespace-nowrap hover:text-primary-600 transition-colors duration-200"
          >
            itaobuy
          </Link>

          {/* Desktop nav items */}
          <div className="hidden md:flex items-center gap-5">
            <Link
              to="/"
              className="text-sm font-medium text-foreground-800 hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap"
            >
              <i className="ri-home-4-line mr-1.5 text-xs"></i>
              {t('nav.home', 'Home')}
            </Link>

            {/* Categories Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                onBlur={() => setTimeout(() => setIsDropdownOpen(false), 200)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-secondary-200/80 text-sm font-medium text-foreground-800 hover:bg-secondary-300 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-grid-line text-xs"></i>
                {t('nav.categories', 'Categories')}
                <i
                  className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${
                    isDropdownOpen ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full mt-2 left-0 bg-background-50 rounded-2xl p-3 w-64 z-50 border border-secondary-200/80 shadow-sm">
                  <div className="grid grid-cols-2 gap-1">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="block px-3 py-2 rounded-lg text-sm text-foreground-700 hover:bg-accent-50 hover:text-foreground-950 transition-colors duration-150 whitespace-nowrap"
                      >
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/blog"
              className="text-sm font-medium text-foreground-800 hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap"
            >
              <i className="ri-book-open-line mr-1.5 text-xs"></i>
              {t('nav.shopping_guide', 'Shopping Guide')}
            </Link>

            <Link
              to="/itaobuyspreadsheet-guide"
              className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors duration-200 whitespace-nowrap"
            >
              {t('nav.blog', 'Blog')}
            </Link>
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center px-4 py-2 rounded-lg border border-secondary-300 text-sm font-medium text-foreground-800 hover:bg-secondary-100 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              {t('nav.itaobuy_sheet', 'itaobuy sheet')}
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center px-5 py-2 rounded-[10px] bg-foreground-950 text-background-50 text-sm font-medium hover:bg-foreground-800 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              {t('nav.buy_now', 'buy it now')}
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`text-lg text-foreground-800 ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu panel */}
      <div
        className={`md:hidden transition-all duration-300 overflow-hidden ${
          isMobileMenuOpen ? 'max-h-[700px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-6 pb-6 bg-background-50 border-t border-secondary-200/60">
          <div className="pt-4 space-y-2">
            <Link
              to="/"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-secondary-100 transition-colors duration-200"
            >
              <i className="ri-home-4-line text-base"></i>
              {t('nav.home', 'Home')}
            </Link>

            {/* Mobile Categories */}
            <button
              onClick={() => setIsMobileCatOpen(!isMobileCatOpen)}
              className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-secondary-100 transition-colors duration-200 cursor-pointer"
            >
              <span className="flex items-center gap-3">
                <i className="ri-grid-line text-base"></i>
                {t('nav.categories', 'Categories')}
              </span>
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${isMobileCatOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {isMobileCatOpen && (
              <div className="grid grid-cols-2 gap-1 px-2 pb-2">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block px-3 py-2.5 rounded-lg text-xs text-foreground-700 hover:bg-accent-50 hover:text-foreground-950 transition-colors duration-150 whitespace-nowrap"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            )}

            <Link
              to="/blog"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-secondary-100 transition-colors duration-200"
            >
              <i className="ri-book-open-line text-base"></i>
              {t('nav.shopping_guide', 'Shopping Guide')}
            </Link>

            <Link
              to="/itaobuyspreadsheet-guide"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-secondary-100 transition-colors duration-200"
            >
              <i className="ri-article-line text-base"></i>
              {t('nav.blog', 'Blog')}
            </Link>

            <div className="pt-3 flex flex-col gap-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center px-5 py-3 rounded-lg border border-secondary-300 text-sm font-medium text-foreground-800 hover:bg-secondary-100 transition-colors duration-200 cursor-pointer"
              >
                {t('nav.itaobuy_sheet', 'itaobuy sheet')}
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center px-5 py-3 rounded-[10px] bg-foreground-950 text-background-50 text-sm font-medium hover:bg-foreground-800 transition-colors duration-200 cursor-pointer"
              >
                {t('nav.buy_now', 'buy it now')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}